import { useEffect, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Link, Modal, ModalBody, Subhead } from '@growtherapy/sprout-ui';

import { Translate } from '../../i18n/Translate';
import { AI_FEATURES_FAQ_LINK } from './constants';

export type ConsentModalProps = {
  onClose: () => void;
  recordOptIn: () => void;
  isOpen: boolean;
  showRefreshedCopy?: boolean;
};

export function ConsentModal({ onClose, recordOptIn, isOpen, showRefreshedCopy = false }: ConsentModalProps) {
  const t = useTranslate();
  const enableButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(
    function focusEnableButtonOnOpen() {
      if (!isOpen || !showRefreshedCopy) return;
      const frame = requestAnimationFrame(() => enableButtonRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    },
    [isOpen, showRefreshedCopy],
  );

  return (
    <Modal
      onClose={onClose}
      onOutsideClick={onClose}
      isOpen={isOpen}
      className='shadow-none sm:max-w-[31rem] md:max-w-[31rem] sm:top-[25%] rebrand:bg-surface-inverse'
    >
      <ModalBody className='flex flex-col items-center gap-8 p-8 text-center'>
        <div className='flex flex-col gap-2 items-center'>
          <Subhead.Default as='h2' className='rebrand:text-text-inverse'>
            {t('telehealth-core:recording.enableTranscriptionSoYouCanFocusOnYourClients')}
          </Subhead.Default>
          {showRefreshedCopy ? (
            <div className='flex w-full flex-col gap-3 text-left'>
              <Body.Default className='rebrand:text-text-inverse'>
                {t('telehealth-core:recording.enablingThisFeatureWillRequestConsentAndShareRecaps')}
              </Body.Default>
              <Body.Default className='rebrand:text-text-inverse'>
                {t('telehealth-core:recording.youCanOptOutOfSessionRecapsForSpecificClients')}
              </Body.Default>
              <Body.Default className='rebrand:text-text-inverse'>
                <Translate
                  i18nKey='telehealth-core:recording.byEnablingAiTranscriptionYoureConfirmingWithLink'
                  components={{
                    1: <Link className='font-normal rebrand:text-text-inverse' external href={AI_FEATURES_FAQ_LINK} />,
                  }}
                />
              </Body.Default>
            </div>
          ) : (
            <Body.Default className='rebrand:text-text-inverse'>
              {t('telehealth-core:recording.enablingThisFeatureWillRequestTranscriptionConsent')}
            </Body.Default>
          )}
        </div>
        <div className='w-full flex  flex-col gap-4 items-center text-center'>
          <div className='flex flex-col sm:flex-row gap-4 sm:gap-8 w-full'>
            <Button onClick={onClose} use='primary' className='w-full rebrand:border-border-default'>
              {t('telehealth-core:recording.noDontEnable')}
            </Button>
            <Button ref={enableButtonRef} onClick={recordOptIn} use='secondary' className='w-full m-0'>
              {t('telehealth-core:recording.yesEnable')}
            </Button>
          </div>
          {!showRefreshedCopy && (
            <Body.Small className='rebrand:text-text-inverse'>
              {t('telehealth-core:recording.youCanChangeThisSettingAnyTimeInYourDashboard')}
            </Body.Small>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}
